import * as React from "react";

import { useTranslation } from "react-i18next";

import { useDispatch, useSelector } from "react-redux";

import { Button, Tabs } from "antd";
import { useState } from "react";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { kmlToGeoJSON, readGeoJson, readKml } from "../FileUtils";
import { addAOIFeature, setDrawingAOIMode } from "../../store/slices/aoiSlice";
import { AOI_FEATURE_KEY } from "./consts";
import { useMapContext } from "../../core/MapContext";
import { flyToGeometry } from "../../../ExploreArchive/ultis";

// A GeoJSON file may be a FeatureCollection, a single Feature, or a bare
// geometry — and each geometry may itself be a Multi* type. Flatten all of
// these into a list of single-part geometries so downstream rendering (Point
// -> teardrop marker via syncPointMarkers, Polygon -> fill/line layers) works
// regardless of the file shape the user uploaded.
const extractGeometries = (geojson: any): any[] =>
{
  if (!geojson) return [];

  // FeatureCollection
  if (Array.isArray(geojson?.features))
  {
    return geojson.features.flatMap((f: any) => extractGeometries(f?.geometry));
  }

  // Single Feature
  if (geojson?.type === "Feature")
  {
    return extractGeometries(geojson?.geometry);
  }

  // GeometryCollection
  if (geojson?.type === "GeometryCollection" && Array.isArray(geojson?.geometries))
  {
    return geojson.geometries.flatMap((g: any) => extractGeometries(g));
  }

  // Split Multi* geometries into single parts.
  if (geojson?.type === "MultiPoint" && Array.isArray(geojson?.coordinates))
  {
    return geojson.coordinates.map((coordinates: any) => ({ type: "Point", coordinates }));
  }
  if (geojson?.type === "MultiPolygon" && Array.isArray(geojson?.coordinates))
  {
    return geojson.coordinates.map((coordinates: any) => ({ type: "Polygon", coordinates }));
  }

  // Bare single-part geometry (Point, Polygon, LineString, ...). Keep only the
  // fields the store/renderers rely on so stray props (crs, bbox) don't leak.
  if (geojson?.type && geojson?.coordinates)
  {
    return [{ type: geojson.type, coordinates: geojson.coordinates }];
  }

  return [];
};

type ParsedAOIFile = {
  /** Single-part geometries the provider accepts — exactly what will be added. */
  geometries: any[];
  /** i18n key of why the file is unusable; null when it can be uploaded. */
  error: string | null;
};

export default function FormUpload(props: any)
{
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { mapInstance } = useMapContext();

  // Geometry types the current provider accepts (from its flow schema). Empty
  // or missing means "no restriction".
  const supportedTypes: string[] = props.supportedTypes ?? [];

  const keepSupported = (geometries: any[]) =>
    supportedTypes.length === 0
      ? geometries
      : geometries.filter((geometry: any) =>
        supportedTypes.some(
          (type) => type.toLowerCase() === String(geometry?.type).toLowerCase(),
        ),
      );

  const [geojsonFile, setGeojsonFile] = React.useState<any>(null);
  const [kmlFile, setKmlFile] = React.useState<any>(null);
  const [activeTab, setActiveTab] = useState<"geojson" | "kml">("geojson");

  // Outcome of reading the picked file. Held here rather than recomputed on
  // confirm so the multi-area warning and any read error are on screen BEFORE
  // the user commits — and so the file is parsed exactly once.
  const [parsed, setParsed] = useState<ParsedAOIFile | null>(null);

  // Guards against an earlier, slower parse landing after a newer pick.
  const latestFileRef = React.useRef<File | null>(null);

  const onClose = () =>
  {
    props.toggleUpload();
  };

  /**
   * Read + normalise a picked file. Runs on selection rather than on confirm so
   * the warning about a multi-area file, and any read error, can be shown while
   * the user can still back out. Never throws — every outcome is a ParsedAOIFile.
   */
  const parseFile = async (file: File, kind: "geojson" | "kml"): Promise<ParsedAOIFile> =>
  {
    try
    {
      let raw: any;

      if (kind === "kml")
      {
        const kmlContent = await readKml(file);
        if (!kmlContent)
        {
          return { geometries: [], error: "Could not read the uploaded file" };
        }
        const geojson: any = kmlToGeoJSON(kmlContent);
        raw = geojson?.[0];
      }
      else
      {
        // Accept FeatureCollection, a single Feature, or a bare geometry.
        const geojson: any = await readGeoJson(file);
        raw = geojson?.geoJSON;
      }

      const found = extractGeometries(raw);

      if (found.length === 0)
      {
        return { geometries: [], error: "No valid geometry found in the uploaded file" };
      }

      const geometries = keepSupported(found);

      if (geometries.length === 0)
      {
        return { geometries: [], error: "This provider does not support the uploaded AOI type" };
      }

      return { geometries, error: null };
    } catch
    {
      return { geometries: [], error: "Could not read the uploaded file" };
    }
  };

  const readFile = () =>
  {
    // The confirm button is disabled in both these cases; this only keeps a
    // stray call from dispatching nothing-shaped features.
    if (!parsed || parsed.error) return;

    parsed.geometries.forEach((geometry: any) =>
    {
      dispatch(
        addAOIFeature({
          id: `${AOI_FEATURE_KEY}-${crypto.randomUUID()}`,
          geometry,
        }),
      );

      dispatch(setDrawingAOIMode('UPLOAD_AOI'))
    });

    // Bring the uploaded geometry into view so the user sees the new AOI
    // (e.g. the teardrop marker for a Point) instead of it landing off-screen.
    flyToGeometry(parsed.geometries[parsed.geometries.length - 1], mapInstance);

    onClose();
  };

  const handleFile = async (file: File) =>
  {
    const ext = file.name.split(".").pop()?.toLowerCase();
    const kind: "geojson" | "kml" | null =
      ext === "kml" ? "kml" : ext === "geojson" || ext === "json" ? "geojson" : null;

    if (!kind)
    {
      // `accept` can be bypassed in the file dialog. Say so rather than silently
      // ignoring the pick and leaving the previous file's result armed.
      setKmlFile(null);
      setGeojsonFile(null);
      latestFileRef.current = file;
      setParsed({ geometries: [], error: "Could not read the uploaded file" });
      return;
    }

    setActiveTab(kind);
    // Only one file is ever uploaded, so clear the other tab rather than leaving
    // a stale name next to a result that no longer describes it.
    setKmlFile(kind === "kml" ? file : null);
    setGeojsonFile(kind === "geojson" ? file : null);
    setParsed(null);

    latestFileRef.current = file;
    const result = await parseFile(file, kind);
    if (latestFileRef.current !== file) return; // superseded by a newer pick

    setParsed(result);
  };

  function UploadAOI({ file, onChange }: any)
  {
    return (
      <div className="py-6 px-2">
        <div className="flex items-center gap-3">
          <input
            id="upload-file"
            type="file"
            accept=".geojson,.kml"
            className="hidden"
            onChange={(e) =>
            {
              if (e.target.files)
              {
                onChange(e.target.files[0]);
              }
            }}
          />

          <label
            htmlFor="upload-file"
            className="px-4 py-2 rounded-full bg-white/80 text-black cursor-pointer hover:bg-white"
          >
            Choose File
          </label>

          <span className="text-gray-300">
            {file ? file.name : "No file chosen"}
          </span>
        </div>

        {file && (
          <section className="py-4 !text-[var(--color-label-form)]">
            <h6 className="text-white text-sm after:content-[''] after:block after:w-full after:border-b after:border-dashed after:border-[#444] after:mt-1">
              {t("File details")}:
            </h6>
            <ul className="mt-2 gap-y-2 text-white">
              <li key={1}>
                {t("Name")}: {file.name}
              </li>
              <li key={2}>
                {t("Type")}: {file.type}
              </li>
              <li key={3}>
                {t("Size")}: {file.size} {t("bytes")}
              </li>
            </ul>
          </section>
        )}
      </div>
    );
  }

  const renderTabs = () =>
  {
    return (
      <>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as any)}
          items={[
            {
              key: "geojson",
              label: "Geojson",
              children: <UploadAOI file={geojsonFile} onChange={handleFile} />,
            },
            {
              key: "kml",
              label: "Kml",
              children: <UploadAOI file={kmlFile} onChange={handleFile} />,
            },
          ]}
        />
      </>
    );
  };

  return (
    <div>
      {renderTabs()}

      {/* An order is placed against ONE area, but a file may hold many. Say so
          here, while the upload can still be cancelled, and point at how to pick. */}
      {parsed && !parsed.error && parsed.geometries.length > 1 && (
        <div className="mb-4 flex gap-2 rounded-lg border border-[#fbbf24]/30 bg-[#fbbf24]/10 px-3 py-2 text-xs text-[#fbbf24]">
          <ExclamationCircleFilled className="mt-[2px] shrink-0" />
          <span>
            {t("This file contains {{total}} areas. An order uses only one area.", {
              total: parsed.geometries.length,
            })}{" "}
            {t("After uploading, click an area in the AOI list to choose it — the selected area is drawn with a solid outline.")}
          </span>
        </div>
      )}

      {parsed?.error && (
        <div className="mb-4 flex gap-2 rounded-lg border border-[#ff6b6b]/30 bg-[#ff6b6b]/10 px-3 py-2 text-xs text-[#ff6b6b]">
          <ExclamationCircleFilled className="mt-[2px] shrink-0" />
          <span>{t(parsed.error)}</span>
        </div>
      )}

      <div className="flex gap-2 justify-end">
        <Button
          className="h-8 w-[100px] btn-outline"
          onClick={() =>
          {
            props?.toggleUpload();
          }}
        >
          {t("Cancel")}
        </Button>
        <Button
          className="h-8 w-[100px]  rounded btn-primary"
          onClick={() =>
          {
            readFile();
          }}
          disabled={!parsed || !!parsed.error}
        >
          {t("Yes")}
        </Button>
      </div>
    </div>
  );
}
